import {
  Component,
  OnInit,
  NgZone,
  ViewChild,
  ElementRef,
} from "@angular/core";
import * as L from "leaflet";
import "leaflet-draw";
import { AppService } from "src/app/shared/services/app.service";
import { User, LoggedUser } from "src/app/shared/interfaces/interfaces";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import * as moment from "moment";
import {
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
} from "@angular/forms";
import * as leafletImage from "leaflet-image";

import { fadeIn } from "src/app/shared/animations/animations";
import html2canvas from "html2canvas";

export interface enhancedUser extends User {
  dist?: { calculated: number };
  age?: number;
}

interface PolygonUserMapping {
  polygon: GeoJSON.Polygon;
  userIds: string[];
  layerId: number;
}

animations: [
  trigger("collapseExpand", [
    state(
      "expanded",
      style({
        width: "*",
        opacity: 1,
        overflow: "hidden",
      })
    ),
    state(
      "collapsed",
      style({
        width: "0",
        opacity: 0,
        overflow: "hidden",
      })
    ),
    transition("expanded <=> collapsed", animate("350ms ease-in-out")),
  ]),
];

@Component({
  selector: "app-overview",
  templateUrl: "./overview.component.html",
  styleUrls: ["./overview.component.scss"],
  animations: [trigger("fadeIn", fadeIn())],
})
export class OverviewComponent implements OnInit {
  constructor(
    private appService: AppService,
    private zone: NgZone,
    private fb: UntypedFormBuilder
  ) {}

  searchForm: UntypedFormGroup;

  problemTypeOptions = [
    { label: "WITH", value: 1, checked: true },
    { label: "WITHOUT", value: 0, checked: false },
  ];
  issuesList = [
    { label: "MOBILITY", value: "Mobility", checked: true },
    { label: "VISION", value: "Vision", checked: true },
    { label: "HEARING", value: "Hearing", checked: true },
    { label: "MENTAL", value: "Mental", checked: true },
    { label: "MENTALHEALTH", value: "MentalHealth", checked: true },
  ];

  mapInit: L.MapOptions;
  mapLayer: L.Layer[] = [];
  mapZoom: number;
  mapCenter: L.LatLngExpression;
  map: L.Map;

  drawOptions: L.Control.DrawConstructorOptions;
  drawnLayers = new L.FeatureGroup();
  drawnPolygons: GeoJSON.Polygon[] = [];

  // Polygon mode tracking
  polygonUserMappings: PolygonUserMapping[] = [];
  allAccumulatedUsers: enhancedUser[] = [];

  activeUsersCount: number;
  users: enhancedUser[] = [];
  loggedUser: LoggedUser;

  showResultsList = false;
  canOverviewCity: boolean;
  // isDrawing = false;
  isDrawingActive = false;

  ngOnInit() {
    this.searchForm = this.fb.group({
      problemType: [[this.problemTypeOptions[0].value], Validators.required],
      issues: this.fb.group({
        selectedIssues: [
          this.issuesList
            .filter((issue) => issue.checked)
            .map((issue) => issue.value),
          this.atLeastOneCheckboxSelected(),
        ],
      }),
      address: ["", Validators.required],
      radius: [200, Validators.required],
      status: [""],
    });

    this.searchForm.get("problemType")!.valueChanges.subscribe((value) => {
      if (!value.includes(1)) {
        this.searchForm.get("issues.selectedIssues")!.setValue([]);
        this.searchForm.get("issues.selectedIssues")!.clearValidators();
        this.searchForm.get("issues")!.disable();
      } else {
        this.searchForm.get("issues")!.enable();
        this.searchForm
          .get("issues.selectedIssues")!
          .setValidators(this.atLeastOneCheckboxSelected());
        this.searchForm
          .get("issues.selectedIssues")!
          .setValue(
            this.issuesList
              .filter((issue) => issue.checked)
              .map((issue) => issue.value)
          );
      }
      this.searchForm.get("issues.selectedIssues")!.updateValueAndValidity();
    });

    const openStreetMaps = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { maxZoom: 19, attribution: "© OpenStreetMap contributors" }
    );

    this.mapInit = {
      layers: [openStreetMaps, this.drawnLayers],
      zoom: 12,
      center: L.latLng(38.254028, 21.7583104),
      preferCanvas: true,
    };

    this.drawOptions = {
      draw: {
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
        marker: false,
        polygon: {
          shapeOptions: {
            renderer: L.canvas(), // 🔥 ADD
          },
        },
      },
      edit: {
        featureGroup: this.drawnLayers,
      },
    };

    this.fetchMunicipalityDetails();
  }

  atLeastOneCheckboxSelected() {
    return (control: any) => {
      const selectedIssues = control.value;
      return selectedIssues && selectedIssues.length > 0
        ? null
        : { atLeastOneSelected: true };
    };
  }

  async fetchMunicipalityDetails() {
    await this.appService.getMunicipalityDetails().subscribe(async (data) => {
      this.mapZoom = data[0].center.zoom;
      this.mapCenter = L.latLng(
        data[0].center.coordinates[1],
        data[0].center.coordinates[0]
      );

      this.canOverviewCity =
        this.appService.loggedUser.info.find(
          (infoObj: any) => infoObj.name === "operator"
        ).obj.status === "accepted";

      this.loggedUser = this.appService.loggedUser;
      this.countActiveUsers();
    });
  }

  addressToMap() {
    // If switching from polygon mode to radius mode, clear all polygon data
    if (this.drawnPolygons.length > 0) {
      this.clearPolygonMode();
    }

    if (!this.searchForm.valid) return;

    this.appService
      .get_coordinates(this.searchForm.get("address")!.value)
      .subscribe(
        (data) => {
          if (data.results.length > 0) {
            this.searchForm.patchValue({
              address: data.results[0].formatted_address,
            });
            this.emergencyPointToMap(
              data.results[0].geometry.location.lat,
              data.results[0].geometry.location.lng
            );
            this.isMapExpanded = false;
            setTimeout(() => {
              this.map?.invalidateSize();
            }, 350);
          }
        },
        (error) => console.error(error)
      );
  }

  exportToExcel() {
    
    const pass_object: any = {
      excelFile: 1, 
    };

    // If polygons exist, use polygon search
    if (this.drawnPolygons.length > 0) {
      pass_object.loc = {
        type: "MultiPolygon",
        coordinates: this.drawnPolygons.map((polygon) => polygon.coordinates),
      };
    } else {

      // radius search parameters when  polygon  
      
      const address = this.searchForm.get("address")!.value;
      if (address) {
        
        this.appService.get_coordinates(address).subscribe(
          (data) => {
            if (data.results.length > 0) {
              const lat = data.results[0].geometry.location.lat;
              const lng = data.results[0].geometry.location.lng;

             
              pass_object.loc = {
                type: "Point",
                coordinates: [lng, lat]
              };
              pass_object.maxdistance = this.searchForm.get("radius")!.value;

              
              this.addCommonFilters(pass_object);

              
              this.executeExcelExport(pass_object);
            }
          },
          (error) => {
            console.error("Error getting coordinates for export:", error);
          }
        );
        return; 
      }
    }

    
    this.addCommonFilters(pass_object);

    console.log("Excel export payload:", pass_object);

   
    this.executeExcelExport(pass_object);
  }

  
  private addCommonFilters(pass_object: any) {
    
    if (this.searchForm.get("problemType")!.value) {
      pass_object.activity_problem = this.searchForm.get("problemType")!.value;
    }

    
    if (
      this.searchForm.get("problemType")!.value?.includes(1) &&
      this.searchForm.get("issues.selectedIssues")!.value?.length > 0
    ) {
      pass_object.disabilities = this.searchForm.get("issues.selectedIssues")!.value;
    }

   
    const statusValue = this.searchForm.get("status")!.value;
    if (statusValue) {
      pass_object.status = statusValue;
    }
  }

  
  private executeExcelExport(pass_object: any) {
    console.log("Excel export payload:", pass_object);

    this.appService.exportToExcelOverview(pass_object).subscribe(
      (blob: Blob) => {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "overview-results.xlsx";
        link.click();
      },
      (error) => console.error("Error exporting to Excel:", error)
    );
  }


  emergencyPointToMap(lat: number, lng: number) {
    if (this.isDrawingActive) return;

    // If switching from polygon mode to radius mode, clear all polygon data
    if (this.drawnPolygons.length > 0) {
      this.clearPolygonMode();
    }

    setTimeout(() => this.focusView([lat, lng]), 1);

    // Clear previous radius search and set up new one
    this.mapLayer = [];
    const circleRad = L.circle([lat, lng], {
      radius: this.searchForm.get("radius")!.value,
      renderer: L.canvas(), // 🔥 IMPORTANT
      color: "#3388ff",
      weight: 3,
      fillOpacity: 0.2,
    });

    this.mapLayer.push(circleRad);
    this.mapLayer.push(
      new L.Marker([lat, lng], {
        icon: L.icon({
          iconSize: [35, 35],
          iconAnchor: [15, 30],
          iconUrl: "assets/images/emergency_blue.png",
        }),
      })
    );

    // Radius search (point-based)
    const searchFilters = {
      loc: { type: "Point", coordinates: [lng, lat] },
      maxdistance: this.searchForm.get("radius")!.value,
      activity_problem: this.searchForm.get("problemType")!.value,
      disabilities: this.searchForm.get("issues.selectedIssues")!.value,
      ...(this.searchForm.get("status")!.value && {
        status: this.searchForm.get("status")!.value,
      }),
    };

    this.searchUsersRadius(searchFilters);
  }

  searchUsersRadius(searchFilters: any) {
    // if (!this.searchForm.valid) return; 

    this.showResultsList = true;
    this.appService.search_users(searchFilters).subscribe(
      (data) => {
        // Sort users by status
        this.users = data.sort((a: enhancedUser, b: enhancedUser) => {
          const statusOrder = { accepted: 0, pending: 1, rejected: 2 };
          const statusA =
            statusOrder[a.status as keyof typeof statusOrder] ?? 3;
          const statusB =
            statusOrder[b.status as keyof typeof statusOrder] ?? 3;
          return statusA - statusB;
        });
      },
      (error) => console.error(error),
      () => {
        // Clear existing user markers
        this.mapLayer = this.mapLayer.filter(
          (layer) =>
            !(
              layer instanceof L.Marker &&
              layer.options.icon?.options.iconUrl?.includes("image")
            )
        );

        // Add user markers for radius search
        this.users.forEach((el) => {
          let iconUrl = "assets/images/image.png";
          switch (el.status) {
            case "accepted":
              iconUrl = "assets/images/image1.png";
              break;
            case "pending":
              iconUrl = "assets/images/image2.png";
              break;
            case "rejected":
              iconUrl = "assets/images/image3.png";
              break;
            default:
              iconUrl = "assets/images/image2.png";
          }

          const userMarker = L.marker(
            [el.loc.coordinates[1], el.loc.coordinates[0]],
            {
              icon: L.icon({
                iconSize: [40, 40],
                iconAnchor: [20, 40],
                iconUrl: iconUrl,
                shadowUrl: "assets/marker-shadow.png",
                shadowAnchor: [15, 40],
              }),
            }
          );

          this.mapLayer.push(userMarker);
          (el as any).age = moment().diff(new Date(el.birthday), "years");
        });
      }
    );
  }

  private clearPolygonMode() {
    // Clear all polygon-related data
    this.drawnPolygons = [];
    this.allAccumulatedUsers = [];
    this.polygonUserMappings = [];
    this.drawnLayers.clearLayers();

    // Clear user markers from polygon searches
    this.mapLayer = this.mapLayer.filter(
      (layer) =>
        !(
          layer instanceof L.Marker &&
          layer.options.icon?.options.iconUrl?.includes("image")
        )
    );

    this.users = [];
  }

  focusView(coordinates: L.LatLngExpression) {
    this.mapCenter = coordinates;
    this.map.invalidateSize();
  }

  onMapReady(map: L.Map) {
    this.map = map;

    if (!this.map.hasLayer(this.drawnLayers)) this.drawnLayers.addTo(this.map);

    map.on("click", (ev: L.LeafletMouseEvent) => {
      this.zone.run(() => {
        if (this.drawnPolygons.length > 0 || this.isDrawingActive) return;

        this.emergencyPointToMap(ev.latlng.lat, ev.latlng.lng);

        this.appService.get_address(ev.latlng.lat, ev.latlng.lng).subscribe(
          (data) => {
            if (data.results.length > 0) {
              this.searchForm.patchValue({
                address: data.results[0].formatted_address,
              });
            }
          },
          (error) => console.error(error)
        );
      });
    });
  }

  onDrawStarted(_e: any) {
    // this.isDrawing = true;
    this.isDrawingActive = true;
    // Don't clear anything here - let the user see what they're working with
  }

  onDrawStopped(_e: any) {
    //this.isDrawing = false;
    this.isDrawingActive = false;
  }

  onDrawCreated(e: L.DrawEvents.Created) {
    this.isDrawingActive = false;
    const layer = e.layer as L.Polygon;
    const latlngs = layer.getLatLngs() as L.LatLng[][];
    const ring = latlngs[0] as L.LatLng[];
    console.log(
      "vertex count:",
      ring.length,
      ring.map((p) => [p.lng, p.lat])
    );

    // If this is the first polygon (transitioning from radius to polygon mode), clear radius search
    if (this.drawnPolygons.length === 0) {
      this.mapLayer = [];
      this.users = [];
    }

    this.drawnLayers.addLayer(layer);

    // Extract polygon and ensure it's closed
    const geoJsonFeature =
      layer.toGeoJSON() as GeoJSON.Feature<GeoJSON.Polygon>;
    const coordinates = geoJsonFeature.geometry.coordinates[0];

    // Ensure polygon is closed (first point = last point)
    if (coordinates.length > 0) {
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];
      if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        coordinates.push([firstPoint[0], firstPoint[1]]);
      }
    }

    const newPolygon: GeoJSON.Polygon = {
      type: "Polygon",
      coordinates: [coordinates],
    };

    this.drawnPolygons.push(newPolygon);

    console.log("Total polygons:", this.drawnPolygons.length);
    console.log("Polygon coordinates:", newPolygon.coordinates);

    // Search for users in this specific polygon only
    this.searchSinglePolygon(newPolygon, L.Util.stamp(layer));
    this.isMapExpanded = false;
    setTimeout(() => {
      this.map?.invalidateSize();
    }, 350);
  }

  onDrawEdited(e: L.DrawEvents.Edited) {
    const editedLayers = e.layers;

    editedLayers.eachLayer((layer: L.Layer) => {
      if (layer instanceof L.Polygon) {
        const layerId = L.Util.stamp(layer);
        const mappingIndex = this.polygonUserMappings.findIndex(
          (m) => m.layerId === layerId
        );

        if (mappingIndex !== -1) {
          // Remove old users from this polygon
          const oldUserIds = this.polygonUserMappings[mappingIndex].userIds;

          // Remove old mapping
          this.polygonUserMappings.splice(mappingIndex, 1);

          // Update polygon geometry in drawnPolygons array
          const polygonIndex = this.drawnPolygons.findIndex((p, idx) => {
            return idx === mappingIndex;
          });

          // Extract polygon and ensure it's closed
          const geoJsonFeature =
            layer.toGeoJSON() as GeoJSON.Feature<GeoJSON.Polygon>;
          const coordinates = geoJsonFeature.geometry.coordinates[0];

          // Ensure polygon is closed (first point = last point)
          if (coordinates.length > 0) {
            const firstPoint = coordinates[0];
            const lastPoint = coordinates[coordinates.length - 1];
            if (
              firstPoint[0] !== lastPoint[0] ||
              firstPoint[1] !== lastPoint[1]
            ) {
              coordinates.push([firstPoint[0], firstPoint[1]]);
            }
          }

          const newPolygon: GeoJSON.Polygon = {
            type: "Polygon",
            coordinates: [coordinates],
          };

          if (polygonIndex !== -1) {
            this.drawnPolygons[polygonIndex] = newPolygon;
          }

          // Remove users that belonged to old polygon
          this.removeUsersFromMap(oldUserIds);

          // Re-search this polygon with new geometry
          this.searchSinglePolygon(newPolygon, layerId);
        }
      }
    });

    console.log("Polygons after edit:", this.drawnPolygons.length);
  }

  onDrawDeleted(e: L.DrawEvents.Deleted) {
    const deletedLayers = e.layers;

    deletedLayers.eachLayer((layer: L.Layer) => {
      if (layer instanceof L.Polygon) {
        const layerId = L.Util.stamp(layer);
        const mappingIndex = this.polygonUserMappings.findIndex(
          (m) => m.layerId === layerId
        );

        if (mappingIndex !== -1) {
          // Remove users associated with this polygon
          const userIds = this.polygonUserMappings[mappingIndex].userIds;

          // Remove mapping
          this.polygonUserMappings.splice(mappingIndex, 1);

          // Remove users from map
          this.removeUsersFromMap(userIds);
        }
      }
    });

    // Rebuild polygon array
    const remainingLayers = this.drawnLayers.getLayers();
    this.drawnPolygons = [];
    remainingLayers.forEach((layer) => {
      if (layer instanceof L.Polygon) {
        const gj = layer.toGeoJSON() as GeoJSON.Feature<GeoJSON.Polygon>;
        this.drawnPolygons.push(gj.geometry);
      }
    });

    console.log("Polygons after delete:", this.drawnPolygons.length);

    // If no polygons remain, reset everything
    if (this.drawnPolygons.length === 0) {
      this.isDrawingActive = false;
      this.showResultsList = false;
      this.mapLayer = [];
      this.users = [];
      this.allAccumulatedUsers = [];
      this.polygonUserMappings = [];

      // Force map to recalculate size after layout change
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 100);
    } else {
      // Update users list
      this.users = this.allAccumulatedUsers;
    }
  }

  private searchSinglePolygon(polygon: GeoJSON.Polygon, layerId: number) {
    this.showResultsList = true;

    const searchFilters = {
      loc: {
        type: "MultiPolygon",
        coordinates: [polygon.coordinates], // Wrap in array for MultiPolygon format
      },
      activity_problem: this.searchForm.get("problemType")!.value,
      disabilities: this.searchForm.get("issues.selectedIssues")!.value,
      ...(this.searchForm.get("status")!.value && {
        status: this.searchForm.get("status")!.value,
      }),
    };

    this.appService.search_users(searchFilters).subscribe(
      (data) => {
        const newUsers = data as enhancedUser[];
        const newUserIds = newUsers.map((u) => u._id);

        // Store mapping of polygon to user IDs
        this.polygonUserMappings.push({
          polygon: polygon,
          userIds: newUserIds,
          layerId: layerId,
        });

        // Accumulate users (avoid duplicates by ID)
        newUsers.forEach((user) => {
          if (!this.allAccumulatedUsers.find((u) => u._id === user._id)) {
            this.allAccumulatedUsers.push(user);
          }
        });

        // Sort accumulated users
        this.allAccumulatedUsers.sort((a, b) => {
          const statusOrder = { accepted: 0, pending: 1, rejected: 2 };
          const statusA =
            statusOrder[a.status as keyof typeof statusOrder] ?? 3;
          const statusB =
            statusOrder[b.status as keyof typeof statusOrder] ?? 3;
          return statusA - statusB;
        });

        this.users = this.allAccumulatedUsers;

        // Add markers for NEW users only
        this.addUserMarkers(newUsers);
      },
      (error) => console.error(error)
    );
  }

  private addUserMarkers(users: enhancedUser[]) {
    users.forEach((el) => {
      // Check if marker already exists
      const markerExists = this.mapLayer.some(
        (layer) => layer instanceof L.Marker && (layer as any).userId === el._id
      );

      if (markerExists) return; // Skip if already on map

      let iconUrl = "assets/images/image.png";
      switch (el.status) {
        case "accepted":
          iconUrl = "assets/images/image1.png";
          break;
        case "pending":
          iconUrl = "assets/images/image2.png";
          break;
        case "rejected":
          iconUrl = "assets/images/image3.png";
          break;
        default:
          iconUrl = "assets/images/image2.png";
      }

      const userMarker = L.marker(
        [el.loc.coordinates[1], el.loc.coordinates[0]],
        {
          icon: L.icon({
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            iconUrl: iconUrl,
            shadowUrl: "assets/marker-shadow.png",
            shadowAnchor: [15, 40],
          }),
        }
      );

      // Store user ID on marker for later identification
      (userMarker as any).userId = el._id;

      this.mapLayer.push(userMarker);
      (el as any).age = moment().diff(new Date(el.birthday), "years");
    });
  }

  private removeUsersFromMap(userIds: string[]) {
    // Check if these users belong to other polygons
    const userIdsToKeep = new Set<string>();
    this.polygonUserMappings.forEach((mapping) => {
      mapping.userIds.forEach((id) => userIdsToKeep.add(id));
    });

    // Remove markers and users that don't belong to any remaining polygon
    userIds.forEach((userId) => {
      if (!userIdsToKeep.has(userId)) {
        // Remove from map
        this.mapLayer = this.mapLayer.filter((layer) => {
          if (layer instanceof L.Marker && (layer as any).userId === userId) {
            this.map.removeLayer(layer);
            return false;
          }
          return true;
        });

        // Remove from accumulated users
        this.allAccumulatedUsers = this.allAccumulatedUsers.filter(
          (u) => u._id !== userId
        );
      }
    });
  }

  private extractPolygonFromLayer(layer: L.Polygon): GeoJSON.Polygon {
    const coordArray: number[][] = [];
    const latlngs = (layer as any).editing.latlngs[0][0];

    latlngs.forEach((coordPair: any) => {
      coordArray.push([coordPair.lng, coordPair.lat]);
    });

    if (coordArray.length > 0) {
      const firstCoord = coordArray[0];
      const lastCoord = coordArray[coordArray.length - 1];
      if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
        coordArray.push([firstCoord[0], firstCoord[1]]);
      }
    }

    return {
      type: "Polygon",
      coordinates: [coordArray],
    };
  }

  countActiveUsers() {
    this.appService
      .count_users(
        this.loggedUser.info.find((el) => el.name === "operator").obj.region
      )
      .subscribe(
        (data) => (this.activeUsersCount = data.countRecord),
        (error) => console.error(error)
      );
  }

  ////// this exists purely to expand map and collapse it
  isMapExpanded = true;

  expandMap() {
    this.isMapExpanded = !this.isMapExpanded;
    setTimeout(() => {
      this.map?.invalidateSize();
    }, 350);
  }

  //// export map to png
  @ViewChild("mapRef") captureMe!: ElementRef<HTMLDivElement>;
  downloadAsPNG() {
    html2canvas(this.captureMe.nativeElement, {
      useCORS: true,
      backgroundColor: null,
      scale: 2,
    }).then((canvas) => {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `capture-${new Date().toISOString().slice(0, 10)}.png`;
      link.click();
    });
  }
}
